import { useQuery } from '@apollo/react-hooks';
import type { ApolloClient } from 'apollo-client';

import { type FeatureId, type ITFGAnalyticsCallbackParams } from '../../common/types';

import { InTrialFeatureGatesBannerQuery } from './constants';
import { type InTrialFeatureGateBannerData } from './types';
import { addOriginToPaymentFlowUrl, getTrialEndDate } from './utils';

export const useGetInTrialFeatureGateBannerData = ({
	// Apps have different ways of getting the AGG client. Its not always as simple as useClient() and depends on the app's architecture.
	// Therefore, we need to pass it in as a prop.
	aggClient,
	tenantId,
	sendOperationalEvent,
	featureId,
	skip = false,
}: {
	aggClient: ApolloClient<any>;
	tenantId: string;
	skip?: boolean;
	featureId: FeatureId;
	sendOperationalEvent: (params: ITFGAnalyticsCallbackParams) => void;
}): InTrialFeatureGateBannerData => {
	const { data, error, loading } = useQuery(InTrialFeatureGatesBannerQuery, {
		client: aggClient,
		variables: {
			cloudId: tenantId,
		},
		skip,
	});

	if (error) {
		sendOperationalEvent({
			type: 'sendOperationalEvent',
			action: 'error',
			actionSubject: 'luna-growth-experiment-graphql-error',
			actionSubjectId: `in-trial-feature-gates-banner-graphql-error`,
			attributes: {
				error: error?.message,
				extraInfo: error?.extraInfo,
				graphqlErrors: error?.graphQLErrors,
				networkErrors: error?.networkError,
				tenantId,
				featureId,
			},
			source: 'in_trial_feature_gates_banner',
		});

		return {
			error,
			loading,
			paymentFlowExperienceUrl: undefined,
			hasPaymentDetailsOnFile: false,
			canUserPayAndUpgrade: false,
			daysLeftInTrial: undefined,
		};
	}

	const invoiceGroup =
		data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.subscription?.accountDetails
			?.invoiceGroup;
	const subscription = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.subscription;
	const experienceCapabilities = invoiceGroup?.experienceCapabilities;

	/**
	 * Calculate core data for the in-trial feature gate banner
	 */
	const paymentFlowExperienceUrl = addOriginToPaymentFlowUrl({
		paymentExperienceUrlV1: experienceCapabilities?.configurePayment?.experienceUrl,
		paymentExperienceUrlV2: experienceCapabilities?.configurePaymentV2?.experienceUrl,
	});
	const hasPaymentDetailsOnFile = !!subscription?.accountDetails?.invoiceGroup?.invoiceable;
	const canUserPayAndUpgrade =
		!!experienceCapabilities?.configurePaymentV2?.isAvailableToUser ||
		!!experienceCapabilities?.configurePayment?.isAvailableToUser;
	const daysLeftInTrial = getTrialEndDate(subscription?.trial?.endTimestamp);

	return {
		error,
		loading,
		paymentFlowExperienceUrl,
		hasPaymentDetailsOnFile,
		canUserPayAndUpgrade,
		daysLeftInTrial,
	};
};
